"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { useToasts } from "@opensea/ui-kit";
import { useLocalStorage } from "@opensea/ui-kit/hooks";
import { useCallback, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants";
import { useSyncFormFields } from "../../../hooks/useSyncFormField";
import { collectionOfferFlowSchema } from "../../../lib/zod/schema";
import { FlowActions, FlowState } from "../../../stores/flowStore";
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange";
import { useItemAttribute, useItemAttributes } from "../../hooks/useItemAttribute";
import { useLiveUpdateCollectionsBestOrders, useLiveUpdateCollectionsBestOrdersFragment } from "../../hooks/useLiveUpdateCollectionsBestOrders";
import { CreateCollectionOfferToast } from "../../ToastHandler/CreateCollectionOfferToast/CreateCollectionOfferToast";
import { DEFAULT_EXPIRATION, OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, OFFER_EXPIRATION_LOCAL_STORAGE } from "../constants";
import { CollectionOfferPriceWarningModal } from "./components/CollectionOfferPriceWarningModal";
import { CreateCollectionOfferFlowReview } from "./components/CreateCollectionOfferFlowReview";
import { CreateCollectionOfferFlowReview_collectionFragment } from "./components/CreateCollectionOfferFlowReview/CreateCollectionOfferFlowReview.fragment";
import { CreateCollectionOfferForm, CreateCollectionOfferFormSkeleton } from "./components/CreateCollectionOfferForm";
import { CreateCollectionOfferFormFragment } from "./components/CreateCollectionOfferForm/CreateCollectionOfferForm.fragment";
const CreateCollectionOfferFlowBaseFragment = graphql(`
    fragment CreateCollectionOfferFlowBase on Collection {
      id
      slug
      imageUrl
      fees {
        totalCreatorFee {
          isRequired
          feeBasisPoints
        }
        openseaFee {
          feeBasisPoints
        }
      }
      floorPrice {
        id
        pricePerItem {
          token {
            unit
          }
        }
      }
      topOffer {
        id
        pricePerItem {
          token {
            unit
          }
        }
      }
      stats {
        totalSupply
      }
      chain {
        identifier
        wrappedNativeCurrency {
          address
        }
        offerCurrency {
          symbol
          address
        }
        nativeCurrency {
          symbol
        }
      }

      ...CreateCollectionOfferForm
      ...CreateCollectionOfferReview_collection
      ...useLiveUpdateCollectionsBestOrders
    }
  `, [CreateCollectionOfferFormFragment, CreateCollectionOfferFlowReview_collectionFragment, useLiveUpdateCollectionsBestOrdersFragment]);
const CreateCollectionOfferQuery = graphql(`
    query CreateCollectionOfferFlowQuery(
      $slug: String!
      $attributesLimit: Int!
    ) {
      collectionBySlug(slug: $slug) {
        __typename
        ... on Collection {
          id
          name
          ...CreateCollectionOfferFlowBase
        }
      }
    }
  `, [CreateCollectionOfferFlowBaseFragment]);
export function CreateCollectionOfferFlow(t0) {
  const $ = _c(8);
  const {
    slug,
    trait,
    traits,
    initialPrice
  } = t0;
  let t1;
  if ($[0] !== slug) {
    t1 = {
      query: CreateCollectionOfferQuery,
      variables: {
        slug,
        attributesLimit: 50
      },
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[0] = slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2] = useQuery(t1);
  const {
    data,
    fetching
  } = t2;
  useEndFlowOnAccountChange();
  if (fetching) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CreateCollectionOfferFormSkeleton />;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  const collection = data?.collectionBySlug?.__typename === "Collection" ? data.collectionBySlug : null;
  if (!collection) {
    throw new Error("Collection not found");
  }
  let t3;
  if ($[3] !== collection || $[4] !== initialPrice || $[5] !== trait || $[6] !== traits) {
    t3 = <CreateCollectionOfferFlowBase collection={collection} initialPrice={initialPrice} trait={trait} traits={traits} />;
    $[3] = collection;
    $[4] = initialPrice;
    $[5] = trait;
    $[6] = traits;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function CreateCollectionOfferFlowBase(t0) {
  const $ = _c(73);
  const {
    collection: collectionKey,
    trait: initialTrait,
    traits: initialTraits,
    initialPrice
  } = t0;
  const {
    addCustomToast
  } = useToasts();
  let t1;
  if ($[0] !== collectionKey) {
    t1 = readFragment(CreateCollectionOfferFlowBaseFragment, collectionKey);
    $[0] = collectionKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = [t1];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [collection] = useLiveUpdateCollectionsBestOrders(t2);
  const offerSymbol = collection.chain.offerCurrency.symbol;
  const t3 = useDefaultFormValues(collection, initialTrait, initialTraits, initialPrice);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = zodResolver(collectionOfferFlowSchema);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = {
      defaultValues: t3,
      mode: "onChange",
      resolver: t4
    };
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const methods = useForm(t5);
  let t6;
  if ($[7] !== methods.control) {
    t6 = {
      control: methods.control,
      name: "offers"
    };
    $[7] = methods.control;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const offers = useWatch(t6);
  const trait = offers[0]?.trait;
  let t7;
  if ($[9] !== offers[0]?.traits) {
    t7 = offers[0]?.traits || [];
    $[9] = offers[0]?.traits;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const traits = t7;
  const isMultiTraitMode = initialTraits !== undefined && initialTraits.length > 1;
  const t8 = trait?.traitType || "";
  const t9 = trait?.value || "";
  const t10 = isMultiTraitMode || !trait;
  let t11;
  if ($[11] !== collection.id || $[12] !== t10 || $[13] !== t8 || $[14] !== t9) {
    t11 = {
      collectionId: collection.id,
      traitType: t8,
      value: t9,
      pause: t10
    };
    $[11] = collection.id;
    $[12] = t10;
    $[13] = t8;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const {
    attribute
  } = useItemAttribute(t11);
  const t12 = !isMultiTraitMode || traits.length === 0;
  let t13;
  if ($[16] !== collection.id || $[17] !== t12 || $[18] !== traits) {
    t13 = {
      traits,
      collectionId: collection.id,
      pause: t12
    };
    $[16] = collection.id;
    $[17] = t12;
    $[18] = traits;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const {
    attributes
  } = useItemAttributes(t13);
  let t14;
  if ($[20] !== attribute?.floorPrice?.token || $[21] !== attributes || $[22] !== isMultiTraitMode) {
    t14 = () => {
      if (!(isMultiTraitMode && Array.isArray(attributes)) || attributes.length === 0) {
        return attribute?.floorPrice?.token.unit;
      }
      if (attributes.length === 1) {
        return attributes[0]?.floorPrice?.token.unit;
      }
      const floorPrices = attributes.map(_temp).filter(_temp2);
      return floorPrices.length > 0 ? Math.max(...floorPrices) : undefined;
    };
    $[20] = attribute?.floorPrice?.token;
    $[21] = attributes;
    $[22] = isMultiTraitMode;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const getAggregateTraitFloorPrice = t14;
  let t15;
  if ($[24] !== attribute?.topOfferPrice?.token || $[25] !== attributes || $[26] !== isMultiTraitMode) {
    t15 = () => {
      if (!(isMultiTraitMode && Array.isArray(attributes)) || attributes.length === 0) {
        return attribute?.topOfferPrice?.token.unit;
      }
      if (attributes.length === 1) {
        return attributes[0]?.topOfferPrice?.token.unit;
      }
      const topOfferPrices = attributes.map(_temp3).filter(_temp4);
      return topOfferPrices.length > 0 ? Math.max(...topOfferPrices) : undefined;
    };
    $[24] = attribute?.topOfferPrice?.token;
    $[25] = attributes;
    $[26] = isMultiTraitMode;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const getAggregateTraitTopOfferPrice = t15;
  const t16 = collection.floorPrice?.pricePerItem.token.unit;
  let t17;
  if ($[28] !== t16) {
    t17 = {
      name: "offers.0.floorPrice",
      value: t16
    };
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const t18 = collection.topOffer?.pricePerItem.token.unit;
  let t19;
  if ($[30] !== t18) {
    t19 = {
      name: "offers.0.bestOffer",
      value: t18
    };
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== getAggregateTraitFloorPrice) {
    t20 = getAggregateTraitFloorPrice();
    $[32] = getAggregateTraitFloorPrice;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t20) {
    t21 = {
      name: "offers.0.traitFloorPrice",
      value: t20
    };
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== getAggregateTraitTopOfferPrice) {
    t22 = getAggregateTraitTopOfferPrice();
    $[36] = getAggregateTraitTopOfferPrice;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t22) {
    t23 = {
      name: "offers.0.traitTopOfferPrice",
      value: t22
    };
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t17 || $[41] !== t19 || $[42] !== t21 || $[43] !== t23) {
    t24 = [t17, t19, t21, t23];
    $[40] = t17;
    $[41] = t19;
    $[42] = t21;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== methods.setValue || $[46] !== t24) {
    t25 = {
      fields: t24,
      setValue: methods.setValue
    };
    $[45] = methods.setValue;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  useSyncFormFields(t25);
  const [view, setView] = useState("create");
  const [isPriceWarningModalOpen, setIsPriceWarningModalOpen] = useState(false);
  const shouldConfirmPrice = offers[0]?.shouldConfirmPrice;
  let t26;
  if ($[48] !== shouldConfirmPrice) {
    t26 = () => {
      if (shouldConfirmPrice) {
        setIsPriceWarningModalOpen(true);
      } else {
        setView("review");
      }
    };
    $[48] = shouldConfirmPrice;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  const handleFormSubmit = t26;
  let t27;
  if ($[50] !== addCustomToast || $[51] !== collection.imageUrl || $[52] !== methods || $[53] !== trait || $[54] !== traits) {
    t27 = () => {
      const {
        receiptStatus
      } = FlowState;
      addCustomToast(() => <CreateCollectionOfferToast imageUrl={collection.imageUrl || ""} price={{
        unit: methods.getValues("offers.0.price"),
        symbol: methods.getValues("offers.0.offerSymbol")
      }} status={receiptStatus} trait={trait} traits={traits} />, {
        id: "create-collection-offer-toast",
        duration: 15000
      });
    };
    $[50] = addCustomToast;
    $[51] = collection.imageUrl;
    $[52] = methods;
    $[53] = trait;
    $[54] = traits;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const showToast = t27;
  let t28;
  if ($[56] !== collection || $[57] !== handleFormSubmit || $[58] !== view) {
    t28 = view === "create" && <CreateCollectionOfferForm collection={collection} onSubmit={handleFormSubmit} />;
    $[56] = collection;
    $[57] = handleFormSubmit;
    $[58] = view;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== collection || $[61] !== showToast || $[62] !== view) {
    t29 = view === "review" && <CreateCollectionOfferFlowReview collection={collection} onBack={() => setView("create")} onError={() => {
      FlowActions.setReceiptStatus("FAILED");
      showToast();
      FlowActions.endFlow();
      setView("error");
    }} onSuccess={() => {
      FlowActions.setReceiptStatus("SUCCESS");
      FlowActions.onSuccess();
      showToast();
      FlowActions.endFlow();
    }} />;
    $[60] = collection;
    $[61] = showToast;
    $[62] = view;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => setView("review");
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== isPriceWarningModalOpen || $[66] !== offerSymbol) {
    t31 = <CollectionOfferPriceWarningModal offerSymbol={offerSymbol} onNext={t30} open={isPriceWarningModalOpen} setIsOpen={setIsPriceWarningModalOpen} />;
    $[65] = isPriceWarningModalOpen;
    $[66] = offerSymbol;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== methods || $[69] !== t28 || $[70] !== t29 || $[71] !== t31) {
    t32 = <FormProvider {...methods}>{t28}{t29}{t31}</FormProvider>;
    $[68] = methods;
    $[69] = t28;
    $[70] = t29;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  return t32;
}
function _temp4(price_0) {
  return typeof price_0 === "number";
}
function _temp3(attr_0) {
  return attr_0?.topOfferPrice?.token.unit;
}
function _temp2(price) {
  return typeof price === "number";
}
function _temp(attr) {
  return attr?.floorPrice?.token.unit;
}
const useDefaultFormValues = (collection, trait, traits, initialPrice) => {
  const $ = _c(23);
  const [localStorageOfferExpirationMinutes] = useLocalStorage(OFFER_EXPIRATION_LOCAL_STORAGE, DEFAULT_EXPIRATION);
  const [localStorageOfferExpirationCustom] = useLocalStorage(OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined);
  let t0;
  if ($[0] !== localStorageOfferExpirationCustom) {
    t0 = localStorageOfferExpirationCustom ? new Date(localStorageOfferExpirationCustom) : undefined;
    $[0] = localStorageOfferExpirationCustom;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== localStorageOfferExpirationMinutes || $[3] !== t0) {
    t1 = {
      expirationType: localStorageOfferExpirationMinutes,
      customExpiration: t0
    };
    $[2] = localStorageOfferExpirationMinutes;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = collection.id;
  const t3 = Boolean(collection.fees.totalCreatorFee.isRequired);
  const t4 = Boolean(collection.fees.totalCreatorFee.isRequired);
  const t5 = collection.fees.totalCreatorFee.feeBasisPoints || 0;
  const t6 = collection.fees.openseaFee?.feeBasisPoints || 0;
  const t7 = initialPrice?.unit || Number.NaN;
  const t8 = collection.floorPrice?.pricePerItem.token.unit;
  const t9 = collection.topOffer?.pricePerItem.token.unit;
  const t10 = collection.chain.offerCurrency.address === collection.chain.wrappedNativeCurrency?.address;
  let t11;
  if ($[5] !== collection.chain.nativeCurrency.symbol || $[6] !== collection.chain.offerCurrency.symbol || $[7] !== collection.id || $[8] !== collection.stats.totalSupply || $[9] !== t10 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7 || $[15] !== t8 || $[16] !== t9 || $[17] !== trait || $[18] !== traits) {
    t11 = [{
      id: t2,
      quantity: 1,
      applyCreatorFee: t3,
      isCreatorFeeRequired: t4,
      creatorFeeBasisPoints: t5,
      openSeaFeeBasisPoints: t6,
      price: t7,
      floorPrice: t8,
      bestOffer: t9,
      maxQuantity: collection.stats.totalSupply,
      trait,
      traits,
      offerSymbol: collection.chain.offerCurrency.symbol,
      nativeCurrencySymbol: collection.chain.nativeCurrency.symbol,
      isWrappedNativeCurrency: t10,
      traitFloorPrice: undefined,
      traitTopOfferPrice: undefined
    }];
    $[5] = collection.chain.nativeCurrency.symbol;
    $[6] = collection.chain.offerCurrency.symbol;
    $[7] = collection.id;
    $[8] = collection.stats.totalSupply;
    $[9] = t10;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = trait;
    $[18] = traits;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t1 || $[21] !== t11) {
    t12 = {
      expiration: t1,
      offers: t11
    };
    $[20] = t1;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
};